import classNames from "classnames";

type ClassValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | ClassValue[]
  | { [key: string]: unknown };

/**
 * Utility function for conditionally joining class names.
 * API-compatible replacement for the Tailwind-based cn() function.
 * Uses classnames library under the hood.
 */
export function cn(...inputs: ClassValue[]): string {
  return classNames(...inputs);
}

// Counter for generating unique IDs
let idCounter = 0;

/**
 * Generates a unique ID string for form elements and components.
 * If no parts are provided, generates a unique ID using an auto-incrementing counter.
 * Otherwise, combines the prefix with the provided parts.
 *
 * @param prefix - The prefix for the ID (e.g., 'checkbox', 'input', 'month-cell')
 * @param parts - Optional additional parts to include in the ID
 * @returns A unique ID string
 *
 * @example
 * ```ts
 * generateId('checkbox'); // "checkbox-1", "checkbox-2", etc.
 * generateId('month-cell'); // "month-cell-1", "month-cell-2", etc.
 * generateId('input', 'email'); // "input-email"
 * generateId('checkbox', 'select-all'); // "checkbox-select-all"
 * ```
 */
export function generateId(
  prefix: string,
  ...parts: (string | number | undefined)[]
): string {
  const validParts = parts.filter(
    (part): part is string | number => part !== undefined,
  );

  if (validParts.length === 0) {
    // No parts provided, generate unique ID with counter
    idCounter++;
    return `${prefix}-${idCounter}`;
  }

  // Parts provided, join them
  return `${prefix}-${validParts.join("-")}`;
}
